/**
 * RevokeMcpClientDialog — confirm-gated revoke with derived effect
 * bullets. Calls-in-last-7d and a portal warning (when the client owns
 * the portal's own OAuth identity) are computed from the DTO.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";
import type { McpClientDTO } from "@/types/edrApi";

const PORTAL_HOSTS = [".lovable.app", "edr.smplify.com"];

function isPortalClient(client: McpClientDTO | null): boolean {
  if (!client?.redirectURIs) return false;
  return client.redirectURIs.some((u) => {
    try {
      const h = new URL(u).hostname.toLowerCase();
      return PORTAL_HOSTS.some((p) => (p.startsWith(".") ? h.endsWith(p) : h === p));
    } catch {
      return false;
    }
  });
}

export interface RevokeMcpClientDialogProps {
  client: McpClientDTO | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  pending?: boolean;
}

export function RevokeMcpClientDialog({
  client,
  open,
  onOpenChange,
  onConfirm,
  pending,
}: RevokeMcpClientDialogProps) {
  const name = client?.displayName ?? "this client";
  const calls7d = client?.callsLast7d ?? 0;
  const portal = isPortalClient(client);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Revoke {name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This client will lose access immediately. In-flight requests will fail
            with 401. The client can re-register via OAuth, but you'll need to
            approve the new registration manually.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="rounded-md border border-border bg-muted/30 px-3 py-2.5 text-xs space-y-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Effects
          </div>
          <ul className="space-y-1 text-foreground">
            <li>• {calls7d.toLocaleString()} calls in the last 7 days will stop</li>
            {portal && (
              <li className="flex items-start gap-1.5 text-warning-muted-foreground">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  The Smplify EDR portal uses this client — revoke will break the
                  dashboard until re-auth.
                </span>
              </li>
            )}
          </ul>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending || !client?.id}
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Revoke
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
